'use client';
import { ChangeEvent, ReactNode, useId } from 'react';
import cn from 'classnames';

import './CheckBox.style.scss';

import TooltipMessage, {
  TooltipPosition,
  tooltipMessageId
} from '@/components/common/TooltipMessage/TooltipMessage';

// Only use this for basic toggling. This is a vanilla HTML/React Checkbox.

// If you're using Formik and using form data, use:
// form/lib/InputField/CheckboxField

type CheckBoxProps = {
  className?: string;
  checked?: boolean;
  label: ReactNode;
  inputAriaLabel?: string;
  disabled?: boolean;
  tooltip?: string;
  tooltipPosition?: TooltipPosition;
  /** When true, the native checkbox can be toggled (no readOnly). Defaults to true when onChange is provided. */
  interactive?: boolean;
  onChange?: (event?: ChangeEvent<HTMLInputElement>) => void;
};

function CheckBox(props: CheckBoxProps) {
  const {
    className = '',
    checked,
    label,
    inputAriaLabel,
    disabled = false,
    tooltip,
    tooltipPosition = 'bottom',
    interactive = !!props.onChange,
    onChange
  } = props;
  const tooltipId = useId();

  return (
    <>
      <div
        className={cn(className, 'InputField', 'CheckBox', {
          disabled,
          TooltipAnchor: Boolean(tooltip)
        })}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      >
        <label className={cn({ cursor: !disabled })}>
          <input
            className={cn({ cursor: !disabled })}
            type='checkbox'
            checked={checked}
            onChange={onChange}
            readOnly={!interactive || disabled}
            disabled={disabled}
            aria-label={inputAriaLabel}
            aria-describedby={tooltip ? tooltipMessageId(tooltipId) : undefined}
          />
          {label}
        </label>
      </div>
      {tooltip && (
        <TooltipMessage
          id={tooltipId}
          content={tooltip}
          position={tooltipPosition}
        />
      )}
    </>
  );
}

export default CheckBox;
